// src/components/PageEngagementTracker.tsx

"use client"

import { useEffect, type ReactNode } from "react"
import { usePathname } from "next/navigation"

import { throttle } from "~/lib/throttle"
import { trackPageEngagement } from "~/lib/gtag"
import { resolvePageExitScrollDepth } from "~/lib/site-intelligence/page-engagement-exit"
import {
  CONSENT_CHANGED_EVENT,
  CONSENT_STORAGE_KEY,
  hasExplicitAnalyticsOptOut,
  isAnalyticsStorageAllowed,
} from "~/lib/analytics/consent"

/**
 * Emits exactly one `page_engagement` event per route visit when the visitor
 * leaves the page (SPA navigation or tab close). Dwell time accumulates across
 * visibility cycles on the same route; tab backgrounding does not emit.
 */
const PageEngagementTracker = (): ReactNode => {
  const pathname = usePathname()

  useEffect(() => {
    if (typeof window === "undefined" || !pathname) return

    // Freeze path + query + full URL at mount — cleanup may run after navigation.
    const frozenSearch = window.location.search || ""
    const frozenPagePath = `${pathname}${frozenSearch}`
    const frozenPageLocation = window.location.href

    let accumulatedVisibleMs = 0
    let visibleSince = Date.now()
    let maxScrollPct = 0
    let emitted = false
    // Frozen while this route is active — SPA cleanup runs after the next page
    // commits, so exit must not re-read the successor page's DOM.
    let pageWasScrollable = false

    const resetPageExitMetrics = (): void => {
      accumulatedVisibleMs = 0
      visibleSince = Date.now()
      maxScrollPct = 0
      emitted = false
      pageWasScrollable = false
    }

    const refreshPageScrollability = (): void => {
      const doc = document.documentElement
      pageWasScrollable = doc.scrollHeight - doc.clientHeight > 0
    }

    const updateScrollMax = (): void => {
      refreshPageScrollability()
      if (!pageWasScrollable) {
        return
      }
      const doc = document.documentElement
      const scrollable = doc.scrollHeight - doc.clientHeight
      const pct = Math.min(100, Math.round((window.scrollY / scrollable) * 100))
      maxScrollPct = Math.max(maxScrollPct, pct)
    }

    const exitScrollDepth = (): { pct: number; synthetic: boolean } =>
      resolvePageExitScrollDepth({ pageWasScrollable, maxScrollPct })

    const captureVisibleElapsed = (): void => {
      accumulatedVisibleMs += Date.now() - visibleSince
      visibleSince = Date.now()
    }

    const totalVisibleMs = (): number => {
      if (document.visibilityState === "visible") {
        return accumulatedVisibleMs + (Date.now() - visibleSince)
      }
      return accumulatedVisibleMs
    }

    const emitEngagement = (options?: { beacon?: boolean }): void => {
      if (emitted) return
      if (document.visibilityState === "visible") {
        captureVisibleElapsed()
      }
      emitted = true
      const scroll = exitScrollDepth()
      trackPageEngagement({
        pagePath: frozenPagePath,
        pageLocation: frozenPageLocation,
        timeOnPageMs: totalVisibleMs(),
        maxScrollDepthPct: scroll.pct,
        syntheticScrollDepth: scroll.synthetic,
        beacon: options?.beacon === true,
      })
    }

    const onVisibility = (): void => {
      if (document.visibilityState === "hidden") {
        captureVisibleElapsed()
      } else {
        visibleSince = Date.now()
      }
    }

    const onPageHide = (event: PageTransitionEvent): void => {
      if (event.persisted) {
        captureVisibleElapsed()
        updateScrollMax()
        emitEngagement({ beacon: true })
        return
      }
      updateScrollMax()
      emitEngagement({ beacon: true })
    }

    const onPageShow = (event: PageTransitionEvent): void => {
      if (!event.persisted) return
      // BFCache restore: prior visit already emitted page_engagement — start fresh counters.
      resetPageExitMetrics()
      refreshPageScrollability()
    }

    const onConsentChange = (): void => {
      if (hasExplicitAnalyticsOptOut() || !isAnalyticsStorageAllowed()) {
        resetPageExitMetrics()
        return
      }
      // Re-grant: start a fresh measurement window without pre-opt-out dwell/scroll.
      resetPageExitMetrics()
      refreshPageScrollability()
    }

    const onStorageConsent = (event: StorageEvent): void => {
      if (event.key === null || event.key === CONSENT_STORAGE_KEY) {
        onConsentChange()
      }
    }

    refreshPageScrollability()
    updateScrollMax()
    const throttledScrollMax = throttle(updateScrollMax, 200)
    window.addEventListener("scroll", throttledScrollMax, { passive: true })
    document.addEventListener("visibilitychange", onVisibility)
    window.addEventListener("pagehide", onPageHide)
    window.addEventListener("pageshow", onPageShow)
    window.addEventListener(CONSENT_CHANGED_EVENT, onConsentChange)
    window.addEventListener("storage", onStorageConsent)

    return () => {
      // Route cleanup runs after the next page commits — do not resample scroll
      // from the new page's DOM; use maxScrollPct captured while this route was active.
      emitEngagement()
      throttledScrollMax.cancel()
      window.removeEventListener("scroll", throttledScrollMax)
      document.removeEventListener("visibilitychange", onVisibility)
      window.removeEventListener("pagehide", onPageHide)
      window.removeEventListener("pageshow", onPageShow)
      window.removeEventListener(CONSENT_CHANGED_EVENT, onConsentChange)
      window.removeEventListener("storage", onStorageConsent)
    }
  }, [pathname])

  return null
}

export default PageEngagementTracker
